import { type InvestmentType } from '@fruitful/types';

import {
    type EmploymentStatus,
    type ExternalAccountLinkingPolicy,
    type InsuranceCoverage,
    type Member,
    type MembershipDetails,
    type OnboardingStatus,
    type TaxFiling,
} from '../../entities/entityTypes';
import { useRefetchMembershipDetailsMutationOptions } from '../../entities/membership/hooks/useRefetchMembershipDetailsMutationOptions';
import { requestBuilder } from '../../http/RequestBuilder';

interface UpdateMemberBody {
    firstName?: string;
    lastName?: string;
    legalFirstName?: string;
    legalLastName?: string;
    dateOfBirth?: string; // iso datetime string
    email?: string;
    phoneNumber?: string;
    disableMfa?: boolean | null;
    onboardingStatus?: OnboardingStatus;
    guideId?: string | null;
    externalAccountLinkingPolicy?: ExternalAccountLinkingPolicy;
    isSubscriptionActive?: boolean | null;
    cancellationReason?: string | null;
    cancellationDate?: string | null; // iso datetime string
    pronouns?: string | null;
    employmentStatus?: EmploymentStatus | null;
    taxFiling?: TaxFiling | null;
    insuranceCoverage?: InsuranceCoverage[];
    investments?: InvestmentType[];
    spinwheelOverride?: boolean | null;
}

const useUpdateMember = requestBuilder.mutation<
    Omit<Member, 'userAddress'>,
    UpdateMemberBody,
    Pick<Member, 'id'>
>({
    endpoint: ({ id }) => `/api/admin/users/${id}`,
    method: 'PATCH',
});

export const useUpdateMemberAndRefetch = (
    membership: MembershipDetails | null,
) => {
    const refetchMembershipMutationOptions =
        useRefetchMembershipDetailsMutationOptions(membership);

    const { mutateAsync } = useUpdateMember(refetchMembershipMutationOptions);

    const updateMemberAsync = async (id: string, body: UpdateMemberBody) => {
        return await mutateAsync({
            urlParams: {
                id,
            },
            body,
        });
    };

    return {
        updateMemberAsync,
    };
};
